import * as React from "react";
import cs from "classnames";
import { Marker } from "../../../api/EvalWorkflowApi";
import { ParamOption, ParamType } from "../../../api/GrammarApi";
import ParamTypeSelectors from "../../../component/param-types/ParamTypeSelectors";

type MarkerSelectedParamsProps = {
  params?: Record<string, string>;
  paramTypes: ParamType[];
};

function MarkerSelectedParams({ params, paramTypes = [] }: MarkerSelectedParamsProps) {
  if (!params || paramTypes?.length < 1) {
    return null;
  }

  const getParamType = (code: string) => paramTypes.find((paramType) => paramType.code === code);
  const getOption = (options: ParamOption[], value: string) => options.find((option) => option.code === value);

  return (
    <div>
      {Object.keys(params)
        .map(getParamType)
        .map((paramType) => (
          <React.Fragment key={paramType.code}>
            {paramType.label} : {getOption(paramType.options, params[paramType.code])?.label || "N/A"}
          </React.Fragment>
        ))}
    </div>
  );
}

type MarkerItemProps = {
  marker: Marker;
  paramTypesByObsTypes?: Record<string, ParamType[]>;
  obsTypes?: string[];
  onMouseEnter?: (evt: React.SyntheticEvent) => void;
  onMouseLeave?: (evt: React.SyntheticEvent) => void;
  onSave?: (marker: Marker) => void;
};

function MarkerItem({
  marker,
  onMouseEnter,
  onMouseLeave,
  obsTypes = ["FRACT"],
  paramTypesByObsTypes = {},
  onSave,
}: MarkerItemProps) {
  const ref = React.useRef<HTMLLIElement>();

  const [isEditing, setIsEditing] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (marker._hoveredFrom === "viewport" && ref) {
      ref.current?.scrollIntoView();
    }
  }, [marker]);

  const handleMouseEnter = (e: React.SyntheticEvent) => {
    setIsEditing(true);
    onMouseEnter(e);
  };

  const handleMouseLeave = (e: React.SyntheticEvent) => {
    setIsEditing(false);
    onMouseLeave(e);
  };

  const handleParamSelect = (paramCode: string, value: string) => {
    onSave?.({
      ...marker,
      valueByParam: {
        ...marker.valueByParam,
        [paramCode]: value,
      },
    });
  };

  const handleObsTypeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const obsType = e.target.value;
    onSave?.({
      ...marker,
      obsType,
      valueByParam:
        paramTypesByObsTypes[obsType]
          ?.filter((param) => param.defaultOpt)
          .reduce((acc, param) => ({ ...acc, [param.code]: param.defaultOpt }), {}) || {},
    });
  };

  return (
    <li
      key={marker._label}
      className={cs("marker-list-item", { "marker-list-item--active": marker._hoveredFrom })}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave} // Use mouseLeave to avoid event bubbling (ie. over selected params triggers mouseOut, not mouseLeave)
      ref={ref}
    >
      {isEditing && obsTypes.length > 1 ? (
        <>
          <label htmlFor="obsType" className="sr-only">
            Obs Type
          </label>
          <select id="obsType" value={marker.obsType} onChange={handleObsTypeSelect}>
            {obsTypes.map((obsType) => (
              <option key={obsType} value={obsType}>
                {obsType}
              </option>
            ))}
          </select>
        </>
      ) : (
        marker._label
      )}
      {isEditing && paramTypesByObsTypes[marker.obsType]?.length > 0 ? (
        <div>
          <ParamTypeSelectors
            paramTypes={paramTypesByObsTypes[marker.obsType]}
            onSelect={handleParamSelect}
            getValue={(paramCode) => marker.valueByParam?.[paramCode]}
          />
        </div>
      ) : (
        <MarkerSelectedParams params={marker.valueByParam} paramTypes={paramTypesByObsTypes[marker.obsType]} />
      )}
    </li>
  );
}

export default React.memo(MarkerItem);
